import React, { useState, lazy } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import 'Components/Cards/FreeProductCard.scss';
import store from 'Redux/store';
import { toggleSelectableFreeProductsModal } from 'Redux/ducks/modal';
import SpecialFormattedNumber from 'Components/Utility/Price/SpecialFormattedNumber';
import FluidImg from 'Components/Utility/FluidImg';
import LazyModal from 'Components/Utility/LazyModal';

const MysteryReveal = lazy(() => import(/* webpackChunkName: "MysteryReveal" */ 'Components/Order/KeyReveals/MysteryReveal'));

const FreeProductCard = ({
  horizontal,
  freeProducts,
  minSpend,
  maxSpend,
  required,
}) => {
  const [open, setOpen] = useState(false);
  const handleModel = () => {
    setOpen(!open);
  };

  /* eslint-disable react/style-prop-object */
  const renderProduct = (product, revealTemplate) => {
    const showButton = product.is_visible || product.free_redeem_code || revealTemplate;

    return (
      <div className={classNames('free-product-container', { 'mb-3': freeProducts.length > 1 })} key={product._id}>
        <div className="cover-container">
          <FluidImg
            src={product.cover}
            alt={product.name}
            subfolder="original"
            className="w-100"
          />
        </div>
        <div className="text-container">
          <div className="free-product-name">
            {product.name}
          </div>
          <div className="price-container">
            <div className="price-free">
              <FormattedMessage
                id="promotion-banner.free-ribbon"
                defaultMessage="FREE"
              />
            </div>
          </div>
        </div>
        {showButton &&
          <React.Fragment>
            <div
              className="btn btn-sm btn-primary view-button"
              key={product._id}
            >
              <FormattedMessage
                id="promotion-banner.button.view"
                defaultMessage="View"
              />
            </div>
          </React.Fragment>
        }
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className={classNames('free-game-promotion-banner', { horizontal })}>
        <div className="promotion-banner-content">
          <div className="title-text-container">
            <div className="icon-title-container">
              <div className="title-container">
                {!required && minSpend && minSpend > 0 &&
                  <div className="description-text">
                    {minSpend < 40 &&
                      <FormattedMessage
                        id="promotion-banner.description.not-required-min-spend.lower"
                        defaultMessage="Get a free bonus with any paid order !"
                      />
                    }
                    {minSpend >= 40 && !maxSpend &&
                      <FormattedMessage
                        id="promotion-banner.description.not-required-min-spend"
                        defaultMessage="Spend over {minSpendAmount} in a one go to get this bonus!"
                        values={{
                          minSpendAmount: <SpecialFormattedNumber value={minSpend / 100} style="currency" />,
                        }}
                      />
                    }
                    {minSpend >= 40 && maxSpend &&
                      <FormattedMessage
                        id="promotion-banner.description.not-required-min-max-spend"
                        defaultMessage="Spend between {minSpendAmount} - {maxSpendAmount} in a one go to get this bonus!"
                        values={{
                          minSpendAmount: <SpecialFormattedNumber value={minSpend / 100} style="currency" />,
                          maxSpendAmount: <SpecialFormattedNumber value={maxSpend / 100} style="currency" />,
                        }}
                      />
                    }
                    &nbsp; (
                    <FormattedMessage
                      id="promotion-banner.description.free-product-limit"
                      defaultMessage="Maximum 3 per customer"
                    />
                    )
                  </div>
                }
                {((required && !minSpend) || (!required && !minSpend)) &&
                  <div className="description-text">
                    <FormattedMessage
                      id="promotion-banner.description.default"
                      defaultMessage="Purchase this product to get this bonus!"
                    />
                    &nbsp; (
                    <FormattedMessage
                      id="promotion-banner.description.free-product-limit"
                      defaultMessage="Maximum 3 per customer"
                    />
                    )
                  </div>
                }
                {required && minSpend && minSpend > 0 &&
                  <React.Fragment>
                    <div className="description-text">
                      <FormattedMessage
                        id="promotion-banner.description.required-min-spend"
                        defaultMessage="Purchase this product and spend over {minSpendAmount} to get this bonus!"
                        values={{
                          minSpendAmount: <SpecialFormattedNumber
                            value={minSpend / 100}
                            style="currency"
                          />,
                        }}
                      />
                      &nbsp;(
                      <FormattedMessage
                        id="promotion-banner.description.free-product-limit"
                        defaultMessage="Maximum 3 per customer"
                      />
                      )
                    </div>
                  </React.Fragment>
                }
              </div>
            </div>
          </div>

          {freeProducts && freeProducts.map((product) => {
            const revealTemplate = product.reveal_template === 'scratchcard' ||
              product.reveal_template === 'present' ||
              product.reveal_template === 'envelope' ||
              product.reveal_template === 'wheel' ||
              product.reveal_template === 'bonus drop' ||
              product.reveal_template === 'metal gear solid';

            let type = 'fallback';
            if (product.is_visible && !product.free_redeem_code && !revealTemplate) {
              type = 'link';
            }
            if (product.free_redeem_code) {
              type = 'choice';
            }
            if (revealTemplate) {
              type = '';
            }

            return (
              <React.Fragment key={product._id}>
                {revealTemplate &&
                  <React.Fragment>
                    <button
                      className="btn-reset-style"
                      key={product._id}
                      onClick={() => handleModel()}
                    >
                      {renderProduct(product, true)}
                    </button>
                    <LazyModal
                      open={open}
                      setOpen={handleModel}
                      className="mystery-test-modal"
                    >
                      <MysteryReveal
                        testReveal
                        testRevealType={product.reveal_template}
                      />
                    </LazyModal>
                  </React.Fragment>
                }

                {type === 'fallback' &&
                  <React.Fragment>
                    {renderProduct(product)}
                  </React.Fragment>
                }

                {type === 'link' &&
                  <Link to={`/${product.type}/${product.slug}`} key={product._id}>
                    {renderProduct(product)}
                  </Link>
                }
                {type === 'choice' &&
                  <button
                    className="btn-reset-style"
                    key={product._id}
                    onClick={() => store.dispatch(toggleSelectableFreeProductsModal({ minSpend }))}
                  >
                    {renderProduct(product)}
                  </button>
                }
              </React.Fragment>
            );
          },
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

FreeProductCard.propTypes = {
  freeProducts: PropTypes.arrayOf(PropTypes.shape({})),
  minSpend: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
  ]),
  maxSpend: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
  ]),
  required: PropTypes.bool,
  horizontal: PropTypes.bool,
};

FreeProductCard.defaultProps = {
  freeProducts: [],
  currencyCode: 'GBP',
  minSpend: 0,
  maxSpend: 0,
  required: false,
  horizontal: false,
};

export default FreeProductCard;
